import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Search } from "lucide-react";
import SearchOverlay from "@/components/SearchOverlay";

const NAV = [
  { label: "Read", to: "/", id: "read" },
  { label: "Locker Room", to: "/locker-room", id: "watch" },
  { label: "Coaches", to: "/category/coaches-corner", id: "coaches" },
  { label: "Parents", to: "/category/parent-sideline", id: "parents" },
  { label: "Resources", to: "/category/practice-plans", id: "resources" },
  { label: "Shop", to: "/shop", id: "shop" },
  { label: "Learn Football", to: "/teach-me-how-to-football", id: "tmhf" },
];

// Scrolls to the site-wide newsletter signup (present in the footer on every page).
function joinTheSideline() {
  const el = document.getElementById("join-the-sideline");
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const input = el.querySelector("input");
    if (input) setTimeout(() => input.focus(), 550);
  }
}

const Wordmark = ({ className = "h-[26px] lg:h-[34px] w-auto" }) => (
  <img src="/brand/logos/voluntoldhq-wordmark-on-dark.svg" alt="VoluntoldHQ" className={className} />
);

export default function Masthead() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileQuery, setMobileQuery] = useState("");
  const navigate = useNavigate();

  const mobileSearch = (e) => {
    e.preventDefault();
    const q = mobileQuery.trim();
    if (q.length >= 2) {
      setOpen(false);
      navigate(`/search?q=${encodeURIComponent(q)}`);
    }
  };

  return (
    <header
      className="sticky top-0 z-40 bg-[color:var(--vt-dark)] vt-grain border-b border-[color:var(--vt-rule-dark)]"
      data-testid="masthead"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[84px]">
          <Link to="/" data-testid="logo-home-link" aria-label="VoluntoldHQ home">
            <Wordmark />
          </Link>

          {/* desktop nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.id}`}
                className={({ isActive }) =>
                  `font-[Nunito_Sans] font-extrabold text-[13px] uppercase tracking-[0.18em] transition-colors ${
                    isActive ? "text-[color:var(--vt-amber)]" : "text-[color:var(--vt-white)] hover:text-[color:var(--vt-amber)]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* desktop CTA */}
          <div className="hidden lg:flex items-center gap-6">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              data-testid="nav-search-btn"
              aria-label="Search"
              className="text-[color:var(--vt-white)] hover:text-[color:var(--vt-amber)] transition-colors"
            >
              <Search size={22} />
            </button>
            <button
              type="button"
              onClick={joinTheSideline}
              data-testid="join-sideline-btn"
              className="inline-flex vt-btn vt-btn-primary vt-btn-sm"
            >
              Join the Sideline <span className="vt-arrow">→</span>
            </button>
          </div>

          {/* mobile controls */}
          <div className="flex items-center gap-3 lg:hidden">
            <button
              type="button"
              onClick={joinTheSideline}
              data-testid="mobile-join-btn"
              className="vt-btn vt-btn-primary vt-btn-sm"
            >
              Join
            </button>
            <button
              className="text-[color:var(--vt-white)]"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </div>

      {/* full-screen mobile menu */}
      {open && (
        <div className="fixed inset-0 z-50 bg-[color:var(--vt-dark)] vt-grain flex flex-col lg:hidden" data-testid="mobile-menu">
          <div className="flex items-center justify-between h-16 px-4 border-b border-[color:var(--vt-rule-dark)]">
            <Wordmark className="h-8 w-auto" />
            <button className="text-[color:var(--vt-white)]" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
              <X size={28} />
            </button>
          </div>
          <div className="flex-1 flex flex-col justify-center px-6 gap-6">
            <form onSubmit={mobileSearch} role="search" className="flex items-center gap-3 border-b-2 border-[color:var(--vt-rule-dark)] pb-3">
              <Search size={22} className="text-[color:var(--vt-amber)] shrink-0" />
              <label htmlFor="mobile-search-input" className="sr-only">Search</label>
              <input
                id="mobile-search-input"
                type="text"
                value={mobileQuery}
                onChange={(e) => setMobileQuery(e.target.value)}
                placeholder="Search…"
                data-testid="mobile-search-input"
                autoComplete="off"
                className="flex-1 min-w-0 bg-transparent border-0 outline-none font-head uppercase text-2xl text-[color:var(--vt-white)] placeholder:text-[color:var(--vt-graphite)]"
              />
            </form>
            {NAV.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.id}`}
                className={({ isActive }) =>
                  `font-head uppercase leading-none text-[50px] transition-colors ${
                    isActive ? "text-[color:var(--vt-amber)]" : "text-[color:var(--vt-white)]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
          <div className="p-6">
            <button
              type="button"
              onClick={() => { setOpen(false); setTimeout(joinTheSideline, 100); }}
              data-testid="mobile-menu-join-btn"
              className="vt-btn vt-btn-primary w-full"
            >
              Join the Sideline <span className="vt-arrow">→</span>
            </button>
          </div>
        </div>
      )}

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
